/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUpload } from '@dm/ang';
import { type Command, type JobResponse, CookieService, I18nService, PermissionService, PromptService, UNPLACEHOLDER } from '@dm/core';
import { PatchService } from 'app/common/services/patch.service';
import { SWITCH } from 'app/config';
import { type Observer, Observable } from 'rxjs';

import { ERROR_OK } from '../../../common/constants';
import { CaUploadService } from './ca-upload.service';

export enum CA_STATUS {
    NOT_EXIST = '0',
    VALID = '1',
    INVALID = '2'
}

@Component({
    selector: 'app-ca-upload',
    templateUrl: './ca-upload.component.html',
    styleUrls: ['./ca-upload.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: CaUploadComponent,
            multi: true,
        },
    ],
})
export class CaUploadComponent implements OnInit {
    @Input() isEdit = true;
    // 证书类型
    @Input() certificateType: number;
    // 证书状态
    @Input() status: CA_STATUS;
    // 是否必填
    @Input() required = true;
    // 用CA控件控制证书与私钥组件是否需要上传
    @Output() onChange: EventEmitter<boolean> = new EventEmitter();

    i18n = this.service.i18n;
    statusText = this.service.statusText;
    CA_STATUS = CA_STATUS;
    UNPLACEHOLDER = UNPLACEHOLDER;
    SWITCH = SWITCH;

    // 控件是否改变
    changed = false;

    // 上传地址
    uploadUrl: string;
    // 错误信息，可用于外部判断是否合法
    errorMsg;
    // 是否重新上传
    isReupload = false;

    hasReadPermission = this.permissionService.hasPermission('certificate_R');

    @ViewChild('uploadEl') uploadEl: FileUpload;

    onSelectPatch: (...args) => void = this.patchService.onSelectPatch.bind(this);

    // 上传observer
    private uploadObserver: Observer<JobResponse>;

    onChangeInterface: (_: boolean) => void = () => null;
    onTouchedInterface: () => void = () => null;

    constructor(
        private service: CaUploadService,
        private cookieService: CookieService,
        private promptService: PromptService,
        private patchService: PatchService,
        private permissionService: PermissionService,
        private i18nService: I18nService,
    ) { }

    ngOnInit(): void {
        this.uploadUrl = this.service.getUploadFileUrl(this.certificateType);
    }

    /**
     * 外部调用进行校验
     */
    validate(): boolean {
        if (this.errorMsg) {
            return false;
        }
        if (this.required && this.uploadEl && !this.uploadEl.files.length) {
            this.errorMsg = this.i18n.requiredErr;
            return false;
        }
        return true;
    }

    /**
     * 返回上传的执行命令，用于配合batch.service使用
     */
    upload(): Command[] {
        const cmds: Command[] = [];

        // 未改变过则不上传
        if (!this.changed) {
            return cmds;
        }

        // 上传文件
        cmds.push({
            id: 'uploadCaFile',
            name: this.i18n.uploadCaFile,
            action: Observable.create((observer) => {
                this.uploadObserver = observer;
                this.uploadEl.onFileSelect({ target: { files: this.uploadEl.files } });
                this.uploadEl.uploadAll();
            }),
        });

        // 校验CA证书安全性
        cmds.push({
            id: 'checkCaFileSecurity',
            deps: ['uploadCaFile'],
            name: this.i18n.checkCaFileSecurity,
            action: this.service.checkSecurity(this.certificateType),
        });

        // 激活CA证书
        cmds.push({
            id: 'activeCaFile',
            deps: ['uploadCaFile', 'checkCaFileSecurity'],
            name: this.i18n.activeMsg,
            beforeAction: (uploadRes, checkSecurityRes) => {
                const { data } = checkSecurityRes;
                let msg;

                // 判断isLowSecurity返回值，若是1，则高危提示根据返回值填充；若为0，则展示一般高危
                if (data.isLowSecurity === '1') {
                    const reasons = JSON.parse(data.reasonOfLowSecurity);
                    const reasonMsgs = reasons.map((reason) => this.service.getReasonOfLowSecurity(reason));
                    const isEN = this.i18nService.language === 'en';
                    msg = this.i18n.replaceWithReasonMsg(isEN ? reasonMsgs.join('. ') : reasonMsgs.join(this.i18n.common));
                } else {
                    msg = this.i18n.replaceMsg;
                }

                return Observable.create((observer) => {
                    this.promptService.warning({
                        id: 'prompt-ca-upload',
                        title: this.i18n.replaceCATitle,
                        content: msg,
                        onOk: () => {
                            this.promptService.close();
                            observer.next();
                            observer.complete();
                        },
                        onCancel: () => {
                            this.promptService.close();
                            observer.error({
                                result: {
                                    code: -1,
                                    description: this.i18n.taskCanceled,
                                },
                            });
                        },
                    });
                });
            },
            action: (uploadRes) => this.service.active(this.certificateType, uploadRes.fileName),
        });

        return cmds;
    }

    // 选择文件
    onSelect(): void {
        if (this.uploadEl.msgs.length > 0) {
            const msg = this.uploadEl.msgs[0];
            this.errorMsg = msg.summary;
            if (msg.detail) {
                this.errorMsg += '<br>' + msg.detail;
            }
        } else {
            this.errorMsg = null;
        }
        // 选中实时刷新文件扩展名
        if (this.uploadEl.files.length > 0) {
            const uploadFileArr = this.uploadEl.files[0].name.split('.');
            this.uploadUrl = this.service.getUploadFileUrl(this.certificateType, uploadFileArr[uploadFileArr.length - 1]);
        }
        this.changed = true;
        this.handleChange();
    }

    // 文件上传使用的是liveUI内部的http接口，需要添加iBaseToken
    onBeforeSend(event: { xhr: { setRequestHeader: (arg0: string, arg1: string) => void } }): void {
        event.xhr.setRequestHeader('iBaseToken', this.cookieService.getToken());
        event.xhr.setRequestHeader('Accept-Language', this.service.getLanguage());
    }

    // CA证书文件上传完成后进行安全校验
    onUpload(event: { file: { fileName: string }; xhr: { response: string } }): void {
        const fileName = event.file.fileName;
        let res;

        if (event.xhr.response) {
            res = {
                result: JSON.parse(event.xhr.response).error,
            };
        } else {
            res = {
                result: {
                    code: ERROR_OK,
                    description: '',
                },
            };
        }

        if (res.result.code === ERROR_OK) {
            this.uploadObserver.next({
                result: res.result,
                fileName,
            });
            this.uploadObserver.complete();
        } else {
            this.uploadObserver.error({
                result: res.result,
            });
        }

        // 恢复upload控件的状态
        setTimeout(() => {
            this.uploadEl.onFileSelect({ target: { files: this.uploadEl.files } });
        });
    }

    // CA证书文件上传失败
    onError(): void {
        this.uploadObserver.error({
            result: {
                code: -999,
                description: this.i18n.uploadFailed,
            },
        });
    }

    // 重新上传
    onReupload(): void {
        this.changed = true;
        this.isReupload = true;
        this.handleChange();
        this.onChange.emit(true);
    }

    // 取消重新上传
    onCancelReupload(): void {
        this.changed = false;
        this.isReupload = false;
        this.errorMsg = null;
        this.handleChange();
        this.onChange.emit(false);
    }

    /**
     * 重置上传状态
     */
    reset(): void {
        this.changed = false;
        this.isReupload = false;
        this.errorMsg = null;
        if (this.uploadEl) {
            this.uploadEl.clear();
            this.uploadEl.clearHistory();
        }
        this.handleChange();
    }

    handleChange(): void {
        this.onChangeInterface(this.changed);
        this.onTouchedInterface();
    }

    writeValue(value: boolean): void {
        this.changed = value;
    }

    registerOnChange(fn: (value: boolean) => void): void {
        this.onChangeInterface = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedInterface = fn;
    }
}
